import React from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { formatExpiry, formatDateTime } from "../../Helpers/SuppressionDuration";

/**
 * Task 9 — full details of one known error, plus its suppression state on each of the
 * organization's jobs.
 *
 * The specification is unusually precise about this dialog: three separate scenarios cover which
 * fields appear. Recommended Fix, Category and the Knowledge Base link are OPTIONAL and must be
 * hidden entirely when absent — not rendered as empty rows — while Error ID, Name, Description
 * and Severity are always shown.
 */

/** Renders a labelled row, or nothing at all when the value is absent. */
function OptionalField({ label, value, isLink }) {
    if (value === null || value === undefined || String(value).trim() === "") return null;
    return (
        <div className="row mb-2">
            <div className="col-4 col-md-3 text-muted">{label}</div>
            <div className="col-8 col-md-9">
                {isLink ? (
                    <a href={value} target="_blank" rel="noopener noreferrer">
                        {value}
                    </a>
                ) : (
                    value
                )}
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div className="row mb-2">
            <div className="col-4 col-md-3 text-muted">{label}</div>
            <div className="col-8 col-md-9">{children}</div>
        </div>
    );
}

export default function KnownErrorDetailsModal({
    show,
    detail,
    loading,
    canChange,
    onRemoveJobSuppression,
    onClose,
}) {
    const suppressedJobs = (detail?.jobs || []).filter((j) => j.isSuppressed);

    return (
        <Modal isOpen={show} toggle={onClose} size="lg" backdrop="static">
            <ModalHeader>
                <span style={{ fontSize: 20 }}>
                    {detail ? `${detail.errorId} — ${detail.name}` : "Known error details"}
                </span>
                <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    style={{ transform: "scale(0.75)", position: "absolute", right: 20, top: 22 }}
                    onClick={onClose}
                />
            </ModalHeader>

            <ModalBody>
                {loading && <p className="text-muted mb-0">Loading…</p>}

                {!loading && !detail && (
                    <p className="text-muted mb-0">This known error could not be loaded.</p>
                )}

                {!loading && detail && (
                    <>
                        {/* Always shown. */}
                        <Field label="Error ID">{detail.errorId}</Field>
                        <Field label="Name">{detail.name}</Field>
                        <Field label="Description">{detail.description}</Field>
                        <Field label="Severity">{detail.severity}</Field>

                        {/* Shown only when populated — see the three BDD scenarios. */}
                        <OptionalField label="Recommended fix" value={detail.recommendedFix} />
                        <OptionalField label="Category" value={detail.category} />
                        <OptionalField
                            label="Knowledge base"
                            value={detail.knowledgebaseArticle}
                            isLink
                        />

                        <hr />

                        <h6 className="mb-3">Suppression</h6>

                        {suppressedJobs.length === 0 ? (
                            <p className="text-muted mb-0">
                                This error is not suppressed on any of your jobs.
                            </p>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-sm align-middle mb-0">
                                    <thead>
                                        <tr>
                                            <th>Job</th>
                                            <th>Suppressed by</th>
                                            <th>Suppressed on</th>
                                            <th>Expires</th>
                                            <th>Reason</th>
                                            {canChange && <th style={{ width: 110 }} />}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {suppressedJobs.map((job) => (
                                            <tr key={job.jobId}>
                                                <td>{job.jobName}</td>
                                                <td>{job.suppressedByEmail}</td>
                                                <td>{formatDateTime(job.createdAt)}</td>
                                                <td>{formatExpiry(job.suppressedUntil)}</td>
                                                {/*
                                                  C5 — the customer's justification. React escapes
                                                  text children, so this is safe as written; never
                                                  switch it to dangerouslySetInnerHTML.
                                                */}
                                                <td>{job.note || "—"}</td>
                                                {canChange && (
                                                    <td>
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => onRemoveJobSuppression(job)}
                                                        >
                                                            Remove
                                                        </button>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </ModalBody>

            <ModalFooter>
                <button className="btn btn-secondary" onClick={onClose}>
                    Close
                </button>
            </ModalFooter>
        </Modal>
    );
}
